import { useState, useEffect, useCallback } from 'react';
import * as api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtNum = (n) => (n || 0).toLocaleString();

const FR_STATUS_COLORS = { new: 'info', reviewed: 'warning', planned: 'success', declined: 'danger' };
const FR_STATUS_OPTIONS = ['new', 'reviewed', 'planned', 'declined'];

const Admin = ({ currentUserId }) => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [featureRequests, setFeatureRequests] = useState([]);
  const [frEdits, setFrEdits] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, u, d, fr] = await Promise.all([
        api.getAdminMetrics(),
        api.getAdminUsers(),
        api.getAdminDocuments(),
        api.getAdminFeatureRequests(),
      ]);
      setMetrics(m.data);
      setUsers(u.data);
      setDocs(d.data);
      setFeatureRequests(fr.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load admin data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const toggleDisabled = async (u) => {
    const verb = u.disabled ? 'enable' : 'disable';
    if (!(await confirm({
      title: `${verb[0].toUpperCase() + verb.slice(1)} account`,
      message: `${verb === 'disable' ? 'Disable' : 'Re-enable'} ${u.email}? ${verb === 'disable' ? 'They will be signed out and unable to log in.' : ''}`,
      confirmLabel: verb[0].toUpperCase() + verb.slice(1),
      danger: verb === 'disable',
    }))) return;
    try {
      await api.setUserDisabled(u._id, !u.disabled);
      showToast(`Account ${verb}d`, 'success');
      setUsers((prev) => prev.map((x) => (x._id === u._id ? { ...x, disabled: !u.disabled } : x)));
    } catch (err) {
      showToast(err.response?.data?.message || `Failed to ${verb}`, 'error');
    }
  };

  const removeUser = async (u) => {
    if (!(await confirm({
      title: 'Delete user',
      message: `Permanently delete ${u.email}? Any home they solely own will be deleted along with all its data. This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    try {
      await api.deleteAdminUser(u._id);
      showToast('User deleted', 'success');
      setUsers((prev) => prev.filter((x) => x._id !== u._id));
      load(); // refresh metrics/docs after cascade
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete user', 'error');
    }
  };

  const saveFrEdit = async (fr) => {
    const edit = frEdits[fr._id] || {};
    try {
      const updated = await api.updateAdminFeatureRequest(fr._id, {
        status: edit.status ?? fr.status,
        adminResponse: edit.adminResponse ?? fr.adminResponse ?? '',
      });
      setFeatureRequests((prev) => prev.map((r) => (r._id === fr._id ? updated.data : r)));
      setFrEdits((prev) => { const n = { ...prev }; delete n[fr._id]; return n; });
      showToast('Saved', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    }
  };

  const removeFr = async (fr) => {
    if (!(await confirm({
      title: 'Delete feature request',
      message: `Delete "${fr.title}"?`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    try {
      await api.deleteAdminFeatureRequest(fr._id);
      setFeatureRequests((prev) => prev.filter((r) => r._id !== fr._id));
      showToast('Deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const removeDoc = async (d) => {
    if (!(await confirm({
      title: 'Delete document',
      message: `Delete "${d.originalName}"? This removes the file permanently.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    try {
      await api.deleteAdminDocument(d._id);
      showToast('Document deleted', 'success');
      setDocs((prev) => prev.filter((x) => x._id !== d._id));
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete document', 'error');
    }
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Admin</h1>
        <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
      </div>

      {metrics && (
        <div className="stats-row">
          <div className="stat-card stat-primary">
            <span className="stat-number">{fmtNum(metrics.users)}</span>
            <span className="stat-label">Users</span>
          </div>
          <div className="stat-card stat-info">
            <span className="stat-number">{fmtNum(metrics.homes)}</span>
            <span className="stat-label">Homes</span>
          </div>
          <div className="stat-card stat-success">
            <span className="stat-number">{fmtNum(metrics.signups.last7)}</span>
            <span className="stat-label">Signups (7d)</span>
          </div>
          <div className="stat-card stat-success">
            <span className="stat-number">{fmtNum(metrics.signups.last30)}</span>
            <span className="stat-label">Signups (30d)</span>
          </div>
          <div className="stat-card stat-warning">
            <span className="stat-number">{fmtNum(metrics.llm.today.calls)}</span>
            <span className="stat-label">AI Calls Today</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{fmtNum(metrics.llm.allTime.calls)}</span>
            <span className="stat-label">AI Calls All-Time</span>
          </div>
        </div>
      )}

      <section style={{ marginTop: '2rem' }}>
        <h2>Users ({users.length})</h2>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th><th>Name</th><th>Joined</th><th>Homes</th>
                <th>AI (today)</th><th>AI (total)</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u._id === currentUserId;
                return (
                  <tr key={u._id} className={u.disabled ? 'admin-row-disabled' : ''}>
                    <td>{u.email}{u.isAdmin && <span className="admin-badge">admin</span>}</td>
                    <td>{u.name || '—'}</td>
                    <td>{fmtDate(u.createdAt)}</td>
                    <td>{u.homeCount}</td>
                    <td>{u.llm.todayCalls}</td>
                    <td>{u.llm.totalCalls} ({fmtNum(u.llm.totalTokens)} tok)</td>
                    <td>{u.disabled ? 'Disabled' : 'Active'}</td>
                    <td>
                      {isSelf || u.isAdmin ? (
                        <span className="admin-muted">—</span>
                      ) : (
                        <div className="admin-actions">
                          <button className="btn btn-secondary btn-sm" onClick={() => toggleDisabled(u)}>
                            {u.disabled ? 'Enable' : 'Disable'}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => removeUser(u)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ marginTop: '2rem' }}>
        <h2>Feature Requests ({featureRequests.length})</h2>
        {featureRequests.length === 0 ? (
          <p className="admin-muted">No submissions yet.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Title</th><th>User</th><th>Category</th><th>Priority</th><th>Status</th><th>Date</th><th>Response</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {featureRequests.map((fr) => {
                  const edit = frEdits[fr._id] || {};
                  const status = edit.status ?? fr.status;
                  const adminResponse = edit.adminResponse ?? fr.adminResponse ?? '';
                  const isDirty = edit.status !== undefined || edit.adminResponse !== undefined;
                  return (
                    <tr key={fr._id}>
                      <td style={{ maxWidth: 200 }}>
                        <span title={fr.description}>{fr.title}</span>
                        {fr.description && <div className="admin-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>{fr.description.slice(0, 80)}{fr.description.length > 80 ? '…' : ''}</div>}
                      </td>
                      <td>{fr.userId?.email || '—'}</td>
                      <td>{fr.category}</td>
                      <td>{fr.priority}</td>
                      <td>
                        <select
                          className="fr-admin-status-select"
                          value={status}
                          onChange={(e) => setFrEdits((prev) => ({ ...prev, [fr._id]: { ...prev[fr._id], status: e.target.value } }))}
                        >
                          {FR_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td>{fmtDate(fr.createdAt)}</td>
                      <td>
                        <input
                          className="fr-admin-response-input"
                          type="text"
                          placeholder="Optional response…"
                          value={adminResponse}
                          onChange={(e) => setFrEdits((prev) => ({ ...prev, [fr._id]: { ...prev[fr._id], adminResponse: e.target.value } }))}
                        />
                      </td>
                      <td>
                        <div className="admin-actions">
                          {isDirty && (
                            <button className="btn btn-primary btn-sm" onClick={() => saveFrEdit(fr)}>Save</button>
                          )}
                          <button className="btn btn-danger btn-sm" onClick={() => removeFr(fr)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ marginTop: '2rem' }}>
        <h2>Recent Uploads ({docs.length})</h2>
        {docs.length === 0 ? (
          <p className="admin-muted">No uploads.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>File</th><th>Home</th><th>Type</th><th>Uploaded</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d._id}>
                    <td>{d.originalName}</td>
                    <td>{d.homeId?.name || '—'}</td>
                    <td>{d.mimetype}</td>
                    <td>{fmtDate(d.createdAt)}</td>
                    <td>
                      <div className="admin-actions">
                        <a className="btn btn-secondary btn-sm" href={api.getDocumentFileUrl(d._id)} target="_blank" rel="noreferrer">View</a>
                        <button className="btn btn-danger btn-sm" onClick={() => removeDoc(d)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Admin;
